import { atom, selector } from "recoil";
import React from "react";
import { 
	createDataRequest, 
	deleteAttachmentRequest,
	updateDataRequest, 
	getClaimTypeListRequest, 
	getAddressListRequest, 
	getDamageBagClaimListRequest, 
	getForwardTypeListRequest, 
	getBagStatusListRequest, 
	getArrivalFlightListRequest,
	getCaseFileHeaderFlightListRequest,
	getDepartureFlightListRequest,
	createNoteRequest
} from "../../../api/controller/CaseFileController";
import { pageState } from "../../../state/GlobalState";
import { permissionState } from "../../../api/state/AuthState";
import moment from "moment";
import { bagArrivalFlightState, bagDepartureFlightState } from "../bags/state";
import {
	getCaseFileOperationalWarnings,
	normalizeBagsFromFormAndApi,
} from "./caseFileOperationalWarnings";
import { CaseFileWarningsIcon, renderCaseFileRemarksLabelPrefix } from "./CaseFileRemarksWarnings";
import { CaseFileMailIcon } from "./CaseFileMailIcon";

export const caseFileListState = atom({
    key: 'caseFileListState',
    default: [],
})
export const caseFileClaimTypeState = atom({
	key: 'caseFileClaimTypeState',
	default:{
		data:[],
		ovf:false,
	}
})
export const caseFileAddressState = atom({
	key: 'caseFileAddressState',
	default:{
		data:[],
		ovf:false,
	}
})
export const caseFileDamageBagClaimState = atom({
	key: 'caseFileDamageBagClaimState',
	default:{
		data:[],
		ovf:false,
	}
})
export const caseFileForwardTypeState = atom({
	key: 'caseFileForwardTypeState',
	default:{
		data:[],
		ovf:false,
	}
})
export const caseFileDPRActionState = atom({
	key: 'caseFileDPRActionState',
	default:{
		data:[],
		ovf:false,
	}
})
export const caseFileBagStatusState = atom({
	key: 'caseFileBagStatusState',
	default:{
		data:[],
		ovf:false,
	}
})
export const caseFileArrivalFlightState = atom({
	key: 'caseFileArrivalFlightState',
	default:{
		data:[],
		ovf:false,
	}
})
// export const bagArrivalFlightState = atom({
// 	key: 'bagArrivalFlightState',
// 	default:{
// 		data:[],
// 		ovf:false,
// 	}
// })
// export const bagDepartureFlightState = atom({
// 	key: 'bagDepartureFlightState',
// 	default:{
// 		data:[],
// 		ovf:false,
// 	}
// })

export const modalConfigDefault = { size: "xl", wizard: false }

export const filtersDefault = [
	{
        label: 'Period',
        size: 6,
        offset: 6,
        required: true,
        type: 'dateRange', // date, dateRange & select only
        value: [moment().subtract(1, 'week').format("YYYY-MM-DD"),moment().format("YYYY-MM-DD")], // String date "yyyy-mm-dd" for date | Array of 2 dates for dateRange | Object as shown for Select { label: "Label", value: 0 }
        list: [], // only for select -> Array should have Objects as shown { label: "Label", value: 0 }
    }
]

// Customer Info a new case is being created from (set by the Customer Info page's "Create case" action).
export const caseFileCustomerInfoLink = { id: null }

const createWithCustomerInfo = async (form) => {
    const response = await createDataRequest(caseFileCustomerInfoLink.id ? { ...form, CustomerInfoId: caseFileCustomerInfoLink.id } : form)
    if(response?.Status === 'success'){
        caseFileCustomerInfoLink.id = null
    }
    return response
}

export const formModes = {
    create: [ { name: 'Default Create', action: createWithCustomerInfo } ], 
    update: [ { name: 'Default Update', action: updateDataRequest, closeOnSubmit: true }, { name: 'Create Note', action: createNoteRequest, closeOnSubmit: true } ],
    view: [ { name: 'Default View' }, { name: 'Receipt' } ],
}

/** Pill colors for `case_files.type` enum (see migration). */
export const CASE_FILE_TYPE_PILL = {
	AHL: { background: "#dbeafe", color: "#1e3a8a", border: "1px solid #93c5fd" },
	DPR: { background: "#ffedd5", color: "#9a3412", border: "1px solid #fdba74" },
	OHD: { background: "#e9d5ff", color: "#581c87", border: "1px solid #c084fc" },
	UNCLAIMED: { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" },
	CLAIMS: { background: "#dcfce7", color: "#14532d", border: "1px solid #86efac" },
};

export const caseFileTypePillBase = {
	display: "inline-block",
	alignSelf: "flex-start",
	fontSize: "0.625rem",
	fontWeight: 600,
	lineHeight: 1.25,
	letterSpacing: "0.02em",
	padding: "1px 6px",
	borderRadius: "999px",
	maxWidth: "100%",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	verticalAlign: "middle",
};

/** Distinct `case_files.case_status` values used in UI (see CaseFile::updateCaseStatus, case report filters). */
const CASE_STATUS_PILL_DEFAULT = {
	background: "#f3f4f6",
	color: "#4b5563",
	border: "1px solid #e5e7eb",
};

const CASE_STATUS_PILL = {
	TRACING: { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" },
	LOCATED: { background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" },
	"PARTIALLY LOCATED": { background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" },
	RECEIVED: { background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" },
	"PARTIALLY RECEIVED": { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" },
	DELIVERED: { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" },
	"PARTIALLY DELIVERED": { background: "#ecfdf5", color: "#15803d", border: "1px solid #a7f3d0" },
	FORWARDED: { background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd" },
	"PARTIALLY FORWARDED": { background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe" },
	"FORWARDED/DELIVERED": { background: "#ccfbf1", color: "#0f766e", border: "1px solid #5eead4" },
};

export function getCaseStatusPillTheme(status) {
	if (status == null || status === "") {
		return CASE_STATUS_PILL_DEFAULT;
	}
	const s = String(status);
	if (CASE_STATUS_PILL[s]) {
		return CASE_STATUS_PILL[s];
	}
	if (s.startsWith("PARTIALLY ")) {
		const rest = s.slice("PARTIALLY ".length);
		const base = CASE_STATUS_PILL[rest];
		if (base) {
			return { ...base, opacity: 0.92 };
		}
	}
	return CASE_STATUS_PILL_DEFAULT;
}

function caseFileFieldAllowedByPermission(field, get) {
	if (!field.permission) return true;
	const page = get(pageState);
	const permissions = get(permissionState);
	let absolute = false;
	let p = field.permission;
	if (p.substr(0, 1) === ':') {
		absolute = true;
		p = p.substr(1);
	}
	p = absolute ? p : `${p}-${page}`;
	return permissions?.includes(p);
}

function filterCaseFileDataStructureByPermission(nodes, get) {
	return nodes
		.filter((node) => caseFileFieldAllowedByPermission(node, get))
		.map((node) => {
			if (!node.children?.length) return node;
			return { ...node, children: filterCaseFileDataStructureByPermission(node.children, get) };
		});
}

export const caseFileDataStructureState = selector({
    key: 'caseFileDataStructureState',
    get: ({ get }) =>
        filterCaseFileDataStructureByPermission([

            { 
				id: "X123",
                label: 'Type',
                type: 'select',
                order: { form: 1, table: 1 },
				table: {
					minWidth: 150,
					cellStyle: {
						display: "flex",
						alignItems: "center",
						height: "100%",
						boxSizing: "border-box",
					},
					customRender: (_value, params) => {
						const caseType = params.data?.type != null && String(params.data.type) !== "" ? String(params.data.type) : "—";
						const typePillTheme = CASE_FILE_TYPE_PILL[caseType] || {
							background: "#f3f4f6",
							color: "#6b7280",
							border: "1px solid #e5e7eb",
						};
						return (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-start",
									width: "100%",
									height: "100%",
									minHeight: 30,
									boxSizing: "border-box",
									padding: "4px 4px 4px 0",
								}}
							>
								<span
									style={{
										...caseFileTypePillBase,
										...typePillTheme,
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										alignSelf: "center",
										marginTop: 0,
										fontSize: "0.675rem",
										fontWeight: 700,
										letterSpacing: "0.05em",
										padding: "0 14px",
										borderRadius: 10,
										minHeight: 30,
										lineHeight: 1.15,
									}}
									title={caseType === "—" ? undefined : caseType}
								>
									{caseType}
								</span>
							</div>
						);
					},
				},
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 3 }, default: 'AHL'  }], 
				update: [{ enabled: true, size: { xs: 12, md: 6, lg: 3 } }, { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				rootValue: true,
				list: {
					ovf: false,
					data:[
						{ label: 'AHL', value: 'AHL' },
						{ label: 'DPR', value: 'DPR' },
						{ label: 'OHD', value: 'OHD' },
						{ label: 'UNCLAIMED', value: 'UNCLAIMED' },
						{ label: 'CLAIMS', value: 'CLAIMS' },
					]
				},
            },
			{ 
                label: 'Reference',
				field: 'primary_reference',
                type: 'text',
                order: { table: 2 }, 
				table: { 
					minWidth: 220,
					maxWidth: 220,
					customRender: (value, params) => {
						const fileRef =
							params.data.file_reference != null &&
							String(params.data.file_reference).trim() !== ""
								? String(params.data.file_reference).trim()
								: null;
						const ref =
							params.data.reference != null &&
							String(params.data.reference).trim() !== ""
								? String(params.data.reference).trim()
								: null;
						return (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								gap: "3px",
								lineHeight: 1.3,
								marginTop: "7px",
								maxWidth: "200px",
								minHeight: 0,
							}}
						>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
									title={fileRef || undefined}
								>
									{fileRef || "—"}
								</span>
								<span
									style={{
										fontSize: "0.6875rem",
										lineHeight: 1.2,
										color: "#6b7280",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
									title={ref || undefined}
								>
									{ref || "—"}
								</span>
						</div>
						);
					},	
				},
                create: [{ enabled: false  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'File Reference',
                type: 'text',
                order: { form: 2.1 }, 
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 3 }, validations: 'hidden_if:Type,CLAIMS,UNCLAIMED', inputNormalize: 'uppercase'  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'Flight Date',
                type: 'datepicker',
                order: { form: 3 }, 
				table: { dateFormat: 'DD-MMM-YY', postDateFormat:'YYYY-MM-DD', },
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 3 } , loadCurrentDate: true, dateFormat: 'YYYY-MM-DD', options: { altInput: true, altFormat: 'F j, Y', dateFormat: 'Y-m-d' } }], 
				update: [{ enabled: true, size: { xs: 12, md: 6, lg: 3 } , dateFormat: 'YYYY-MM-DD', options: { altInput: true, altFormat: 'F j, Y', dateFormat: 'Y-m-d' } }, { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				child: {
					name: "Flight",
					subFields: { label: "name", value: "name" },
					mergeFormKeys: ['Type'],
					fn: getCaseFileHeaderFlightListRequest
				}
            },
			{ 
                label: 'Flight',
                type: 'select',
				rootValue: true,
                order: { table: 3, form: 4 }, 
				table: {
					minWidth: 120,
					maxWidth: 120,
					customRender: (value, params) => {
						const flight = params.data.flight;
						const flightLine = flight != null && flight !== '' ? String(flight) : '—';
						const dateRaw = params.data.flight_date;
						const dateLine = dateRaw ? moment(dateRaw).format('DD-MMM-YY') : '—';
						return (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									gap: "2px",
									lineHeight: 1.3,
									marginTop: "7px",
									minHeight: 0,
								}}
							>
								<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{flightLine}
								</span>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										fontSize: "0.8125rem",
									}}
								>
									{dateLine}
								</span>
							</div>
						);
					},
				},
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 3 }, validations: 'disabled_if:FlightDate,null', creatable: true  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				list: get(caseFileArrivalFlightState)
            },
			{ 
                label: 'Claim Type',
                type: 'select',
                order: { form: 5 }, 
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:Type,!CLAIMS'  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				list: get(caseFileClaimTypeState),
				getRequest: getClaimTypeListRequest,
            },
			{ 
                label: 'Claimed Item',
                type: 'text',
                order: { form: 6 }, 
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 8 }, validations: 'hidden_if:Type,!CLAIMS'  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'Case Status',
				tableHeader: 'Status',
                type: 'text',
                order: { table: 4.5, }, 
				table: {
					minWidth: 300,
					maxWidth: 300,
					customRender: (value, params) => {
						const status =
							params.data.case_status != null && params.data.case_status !== ""
								? String(params.data.case_status)
								: "—";
						const rec = Number(params.data.number_of_received_bags ?? 0);
						const tot = Number(params.data.number_of_bags ?? 0);
						const ratio = Number.isFinite(tot) ? `${rec}/${tot}` : "—";
						const closed = !!params.data.closed;
						const pillTheme = getCaseStatusPillTheme(status === "—" ? "" : status);
						const rowWarnings = getCaseFileOperationalWarnings({
							type: params.data?.type,
							departureDate: params.data?.departure_date,
							flightDate: params.data?.flight_date,
							closed: params.data?.closed,
							caseStatus: params.data?.case_status,
							bags: normalizeBagsFromFormAndApi(null, params.data?.bags),
						});
						const isCourtesy = Boolean(params.data?.courtesy);
						return (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "flex-start",
									justifyContent: "center",
									gap: "3px",
									lineHeight: 1.3,
									marginTop: "7px",
									minHeight: 0,
									maxWidth: "100%",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										flexWrap: "wrap",
										gap: "6px",
										minWidth: 0,
										alignSelf: "flex-start",
										maxWidth: "100%",
									}}
								>
									{closed ? (
										<i
											className="mdi mdi-lock-outline"
											title="Closed case"
											aria-hidden
											style={{
												fontSize: "15px",
												color: "#6b7280",
												flexShrink: 0,
												lineHeight: 1,
											}}
										/>
									) : null}
									<span
										style={{
											...caseFileTypePillBase,
											fontSize: "0.8125rem",
											fontWeight: 700,
											letterSpacing: "0.03em",
											padding: "3px 10px",
											...pillTheme,
											flex: "0 1 auto",
											width: "fit-content",
											maxWidth: "100%",
										}}
										title={status}
									>
										{status}
									</span>
									{isCourtesy ? (
										<span
											style={{
												display: "inline-flex",
												alignItems: "center",
												justifyContent: "center",
												boxSizing: "border-box",
												minWidth: 24,
												minHeight: 24,
												padding: "0 6px",
												borderRadius: 999,
												background: "#ecfdf5",
												color: "#047857",
												border: "1px solid #a7f3d0",
												lineHeight: 1,
												flexShrink: 0,
											}}
											title="Courtesy"
											aria-label="Courtesy"
										>
											<i
												className="mdi mdi-handshake-outline"
												style={{ fontSize: "0.9rem", lineHeight: 1 }}
												aria-hidden
											/>
										</span>
									) : null}
									<CaseFileMailIcon mailLogs={params.data?.case_mail_logs || []} />
									<CaseFileWarningsIcon warnings={rowWarnings} compact />
								</div>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "4px",
										fontSize: "0.6875rem",
										lineHeight: 1.2,
										color: "#6b7280",
										whiteSpace: "nowrap",
									}}
									title="Received bags / total bags"
								>
									<i
										className="mdi mdi-bag-suitcase-outline"
										aria-hidden
										style={{
											fontSize: "0.875rem",
											color: "#6b7280",
											lineHeight: 1,
											opacity: 0.9,
										}}
									/>
									<span>{ratio} rcv</span>
								</div>
							</div>
						);
					},
				},
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'First name',
				name: 'FirstName',
				field: 'first_name',
				tableHeader: 'Name',
                type: 'text',
                order: { table: 5, form: 8 }, 
				table: {
					customRender: (value, params) => {
						const nameLine =
							params.data.full_name != null && params.data.full_name !== ''
								? String(params.data.full_name)
								: '—';
						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									lineHeight: 1.3,
									marginTop: "7px",
									minHeight: 0,
								}}
							>
								<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{nameLine}
								</span>
							</div>
						);
					},
				},
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, inputNormalize: 'uppercase_letters_spaces'  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{
				label: 'Last name',
				name: 'LastName',
				field: 'last_name',
				type: 'text',
				order: { form: 8.5 },
				create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, inputNormalize: 'uppercase_letters_spaces' }],
				update: ['same_as:create.0', { enabled: false }],
				view: ['same_as:create.0', { enabled: false }],
			},
			{ 
                label: 'Address',
                field: 'address',
                type: 'select',
                order: { form: 9, table: 5.5 }, 
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				table: {
					customRender: (value, params) => {
						const addr = params.data.address;
						let addressLine = '—';
						if (addr != null) {
							if (typeof addr === 'object') {
								const label = addr.name ?? addr.label ?? '';
								addressLine = label !== '' ? String(label) : '—';
							} else {
								addressLine = String(addr);
							}
						}
						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									lineHeight: 1.3,
									marginTop: "7px",
									minHeight: 0,
								}}
							>
								<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{addressLine}
								</span>
							</div>
						);
					},
				},
				list: get(caseFileAddressState),
				getRequest: getAddressListRequest,
            },
			{ 
                label: 'Bag Count',
				field: 'number_of_bags',
                type: 'number',
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'User',
                type: 'select',
                order: { form: 10 }, 
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 } }, { enabled: false }], 
            },
			{ 
                label: 'Departure Date',
                type: 'datepicker',
                order: { form: 11 }, 
				table: { dateFormat: 'MMM Do, YYYY', postDateFormat:'YYYY-MM-DD' },
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 } , dateFormat: 'YYYY-MM-DD', options: { altInput: true, altFormat: 'F j, Y', dateFormat: 'Y-m-d', allowFutureDates: true } }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'Damage Bag Claim',
                type: 'select',
                order: { form: 12 }, 
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:Type,!DPR'  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				list: get(caseFileDamageBagClaimState),
				getRequest: getDamageBagClaimListRequest,
            },
			{ 
                label: 'Courtesy',
                type: 'checkbox',
                order: { form: 12.5 }, 
                create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, default: false }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'Bags',
                type: 'repeater',
                order: { form: 13 },
                create: [{ enabled: true, size: { xs: 12, md: 12, lg: 12 }, deleteBtnSize: { xs: 2, md: 1 }, repeatLabel: true, divider: true }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', 'same_as:create.0'],
                children: [
                    { 
                		label: 'Tag Number',
                		type: 'text',
                		order: { form: 1 }, 
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', 'same_as:create.0'], 
            		},
					{
						label: 'Color Type',
						field: 'color_type',
						type: 'text',
						order: { form: 1.05 },
						create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, creatable: true }],
						update: ['same_as:create.0', { enabled: false }],
						view: ['same_as:create.0', 'same_as:create.0'],
					},
					{ 
                		label: 'Bag Status',
                		type: 'select',
                		order: { form: 1.1 }, 
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, default: 1, validations: 'hidden'  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', 'same_as:create.0'], 
						list: get(caseFileBagStatusState),
						getRequest: getBagStatusListRequest,
            		},
					{ 
                		label: 'Arriving Flight',
						name: 'LocationHistory',
						field: 'location_history',
                		type: 'text',
                		order: { form: 1.2 },
						permission: ':locate-caseFiles',
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,TRACING,PROCESSING|hidden_if:BagCaseType,DPR,OHD,UNCLAIMED' }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            		},
					{ 
                		label: 'Receive Date',
                		type: 'datepicker',
                		order: { form: 2 },
						permission: ':receive-caseFiles',
						table: { dateFormat: 'MMM Do, YYYY', postDateFormat:'YYYY-MM-DD' },
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,TRACING,LOCATED,PROCESSING', dateFormat: 'YYYY-MM-DD', options: { altInput: true, altFormat: 'F j, Y', dateFormat: 'Y-m-d' } }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
						child: {
							name: "ReceivedFlight",
							subFields: { label: "name", value: "name" }, 
							fn: getArrivalFlightListRequest
						}
            		},
					{ 
                		label: 'Received Flight',
                		type: 'select',
						rootValue: true,
                		order: { form: 3 },
						permission: ':receive-caseFiles',
                		create: [{ enabled: true, creatable: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,TRACING,LOCATED,PROCESSING|hidden_if:BagCaseType,UNCLAIMED,OHD|disabled_if:ReceiveDate,null' }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
						list: get(bagArrivalFlightState)
            		},
					{
						label: 'Received Weight (kg)',
						name: 'ReceivedWeight',
						field: 'received_weight',
						type: 'number',
						order: { form: 3.5 },
						permission: ':receive-caseFiles',
						create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,TRACING,LOCATED,PROCESSING', default: '' }],
						update: ['same_as:create.0', { enabled: false }],
						view: ['same_as:create.0', { enabled: false }],
					},
					{ 
                		label: 'Forward Date',
                		type: 'datepicker',
                		order: { form: 5 },
						permission: ':forward-caseFiles',
						table: { dateFormat: 'MMM Do, YYYY', postDateFormat:'YYYY-MM-DD' },
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!FORWARDED', dateFormat: 'YYYY-MM-DD', options: { altInput: true, altFormat: 'F j, Y', dateFormat: 'Y-m-d' } }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
						child: {
							name: "ForwardFlight",
							subFields: { label: "name", value: "name" }, 
							fn: getDepartureFlightListRequest
						}
            		},
					{ 
                		label: 'Forward Flight',
                		type: 'select',
						rootValue: true,
                		order: { form: 6 },
						permission: ':forward-caseFiles',
                		create: [{ enabled: true, creatable: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!FORWARDED|disabled_if:ForwardDate,null' }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
						list: get(bagDepartureFlightState),
            		},
					{ 
                		label: 'Forward Type',
                		type: 'select',
                		order: { form: 7 },
						permission: ':forward-caseFiles',
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!FORWARDED' }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
						list: get(caseFileForwardTypeState),
						getRequest: getForwardTypeListRequest,
            		},
					{ 
                		label: 'Rush Tag Number',
                		type: 'text',
                		order: { form: 8 },
						permission: ':forward-caseFiles',
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!FORWARDED' }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            		},
					{ 
                		label: 'Deliver Date',
                		type: 'datepicker',
						field: 'deliver_date',
                		order: { form: 8.1 },
						permission: ':deliver-caseFiles',
						table: { dateFormat: 'HH:mm | MMM Do', postDateFormat:'YYYY-MM-DD HH:mm' },
                		create: [{ enabled: false }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!DELIVERED' , dateFormat: 'YYYY-MM-DD HH:mm', options: { altInput: true, enableTime: true, time_24hr: true, altFormat: 'H:i | F j, Y', dateFormat: 'Y-m-d H:i' } }, 'same_as:view.0'], 
            		},
					{ 
                		label: 'Delivery Name',
                		type: 'text',
                		order: { form: 8.2 },
						permission: ':deliver-caseFiles',
                		create: [{ enabled: false }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!DELIVERED' }, 'same_as:create.0'], 
            		},
					{ 
                		label: 'Delivery Contact',
                		type: 'text',
                		order: { form: 8.3 },
						permission: ':deliver-caseFiles',
                		create: [{ enabled: false }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!DELIVERED' }, 'same_as:create.0'], 
            		},
					{ 
                		label: 'Delivery Identity',
                		type: 'text',
                		order: { form: 8.4 },
						permission: ':deliver-caseFiles',
                		create: [{ enabled: false }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!DELIVERED' }, 'same_as:create.0'], 
            		},
					{ 
                		label: 'Delivery Condition',
                		type: 'text',
                		order: { form: 8.41 },
						permission: ':deliver-caseFiles',
                		create: [{ enabled: false }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!DELIVERED' }, 'same_as:create.0'], 
            		},
					{ 
                		label: 'Delivery Signature',
                		type: 'signature',
                		order: { form: 8.5 },
						permission: ':deliver-caseFiles',
                		create: [{ enabled: false }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagStatus,!DELIVERED' }, 'same_as:create.0'], 
            		},
					{ 
                		label: 'Damage Bag Receive Date',
                		type: 'datepicker',
                		order: { form: 8.01 },
						permission: ':receive-caseFiles',
                		create: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:BagCaseType,!DPR', dateFormat: 'YYYY-MM-DD', options: { altInput: true, altFormat: 'F j, Y', dateFormat: 'Y-m-d' } }], 
						update: ['same_as:create.0', { enabled: false }], 
						view: [{ enabled: true, size: { xs: 12, md: 6, lg: 4 }, validations: 'hidden_if:Action,null' }, { enabled: false }],
            		},
					
                ]
            },
			{ 
                label: 'Remarks',
                type: 'textarea',
                order: { form: 14.1 }, 
				labelPrefix: renderCaseFileRemarksLabelPrefix,
                create: [{ enabled: true, size: { xs: 12 }, subText: "for Airline", attributes: { rows: 4 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
            },
			{ 
                label: 'Attachments',
                type: 'dropzone',
                order: { form: 15 }, 
                create: [{ enabled: true, size: { xs: 12 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0', { enabled: false }], 
				deleteFn: deleteAttachmentRequest,
            },
			{ 
                label: 'Notes',
                type: 'repeater',
                order: { form: 15.01 },
                create: [{ enabled: false }], update: [{ enabled: false }, { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 12, lg: 12 }, deleteBtnSize: { xs: 2, md: 1 }, repeatLabel: true, divider: true }, { enabled: false }],
                children: [
                    { 
                		label: 'Note',
                		type: 'text',
                		order: { form: 1 }, 
                		create: [{ enabled: false }], update: [{ enabled: false }, { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6, lg: 8 }  }, { enabled: false }], 
            		},
                    { 
                		label: 'User',
                		type: 'select',
                		order: { form: 2 }, 
                		create: [{ enabled: false }], update: [{ enabled: false }, { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 3, lg: 2 }  }, { enabled: false }], 
            		},
                    { 
                		label: 'Time',
                		type: 'datepicker',
						field: 'created_at',
                		order: { form: 3 }, 
                		create: [{ enabled: false }], update: [{ enabled: false }, { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 3, lg: 2 }, dateFormat: 'MMM Do, HH:mm' }, { enabled: false }], 
            		},
				]
			},

			{
				label: 'Note',
				type: 'textarea',
				order: { form: 15.1 }, 
				create: [{ enabled: false }], 
				update: [
					'same_as:create.0',
					{ enabled: true, size: { xs: 12 } },
				], 
				view: ['same_as:create.0', { enabled: false }],
            },




        ], get)
})
